import { useEffect, useSyncExternalStore } from 'react';
import { checkoutProgress, clearCheckoutProgress } from '../checkout/checkout-progress.js';
import { createFundingNavigation, } from './navigation.js';
const navigation = new Map();
export function useFundingNavigation(client, origin = 'deposit', purchase) {
    const key = origin === 'deposit'
        ? purchase
            ? `${client.progressKey ?? client.cacheKey}:${JSON.stringify({ marketAddress: purchase.marketAddress, outcomeIndex: purchase.outcomeIndex, ...(purchase.preferredMethod ? { preferredMethod: purchase.preferredMethod } : {}) })}`
            : (client.progressKey ?? client.cacheKey)
        : `${client.progressKey ?? client.cacheKey}:withdraw`;
    const now = Date.now();
    let entry = navigation.get(key);
    if (!entry || (entry.subscribers === 0 && entry.expiresAt < now)) {
        for (const [scope, value] of navigation)
            if (value.subscribers === 0 && value.expiresAt < now)
                navigation.delete(scope);
        const base = createFundingNavigation(origin, Boolean(purchase));
        const clearDraft = () => {
            const progress = checkoutProgress(key);
            progress.method = null;
            progress.amountUnits = null;
            progress.verificationRequested = false;
            progress.verificationFromProfile = false;
            progress.verificationFromLimit = false;
        };
        const controller = {
            ...base,
            deposit: () => {
                clearDraft();
                base.deposit();
            },
        };
        controller.back = () => {
            const revision = base.getSnapshot().revision;
            base.back();
            if (base.getSnapshot().revision !== revision)
                clearDraft();
        };
        controller.restore = (retainUnsubmitted = false) => {
            const revision = base.getSnapshot().revision;
            base.restore(retainUnsubmitted);
            if (base.getSnapshot().revision !== revision)
                clearDraft();
        };
        const retained = { expiresAt: now + 30 * 60 * 1000, subscribers: 0, controller };
        controller.subscribe = (listener) => {
            retained.subscribers += 1;
            const unsubscribe = base.subscribe(listener);
            return () => {
                unsubscribe();
                retained.subscribers -= 1;
                retained.expiresAt = Date.now() + 30 * 60 * 1000;
            };
        };
        entry = retained;
        navigation.set(key, retained);
    }
    const controller = entry.controller;
    const state = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
    return { ...state, ...controller };
}
/** History uses the same receipt origin rules whether opened from Deposit or a receipt link. */
export function useFundingHistoryNavigation(client, open, initialOperationId, origin = 'deposit') {
    const route = useFundingNavigation(client, origin);
    useEffect(() => {
        if (!open)
            return;
        if (initialOperationId)
            route.openReceipt(initialOperationId, 'history');
        else
            route.openHistory();
    }, [open, initialOperationId, route.openHistory, route.openReceipt]);
    return route;
}
/** Host auth logout hook; ordinary modal teardown deliberately preserves recovery. */
export function clearFundingProgress() {
    clearCheckoutProgress();
    navigation.clear();
}
